import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, ArrowDown, Banknote, Building2, ShoppingCart,
  ShieldCheck, Sparkles, CreditCard, Wallet, FileText, Zap, Lock,
  CircleDollarSign, Clock, Bot, Layers,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────
// verqo Flow of Funds — single-page diagram for sharing with banking /
// ledger partners (Modern Treasury, Plaid). Shows every leg of money
// movement: who pays whom, when, in what currency, through which provider.
// ──────────────────────────────────────────────────────────────────────────

const FLOWS = [
  {
    id: "happy-path",
    title: "Happy path — Buyer pays invoice, Seller paid D+1",
    desc: "Default flow. Buyer remits → tokenized at the card/ACH gateway → Modern Treasury holds in verqo operating account → ledgered → payout to seller bank D+1.",
    legs: [
      { from: "Buyer bank",      to: "verqo Operating (MT)", amount: "$10,000", method: "ACH debit (gateway → MT)",     time: "T+0",  ledger: "credit: AR · debit: Cash held" },
      { from: "verqo Operating", to: "verqo Fee account",    amount: "$200",     method: "Internal transfer",         time: "T+0",  ledger: "credit: Cash held · debit: Platform revenue" },
      { from: "verqo Operating", to: "Seller bank",          amount: "$9,800",   method: "ACH credit (MT payment order)", time: "T+1", ledger: "credit: Cash held · debit: AP" },
    ],
    providers: ["Modern Treasury", "ACH rail"],
  },
  {
    id: "credit",
    title: "Net-30/60/90 — Platform fronts cash, buyer settles later",
    desc: "Buyer chooses terms at checkout. verqo advances seller on D+1. Buyer obligation captured as a receivable. Auto-debit on due date.",
    legs: [
      { from: "verqo Capital pool (MT virtual)", to: "Seller bank",           amount: "$9,800",   method: "ACH credit (MT)",     time: "T+1",  ledger: "credit: Capital pool · debit: AP. Open AR vs Buyer." },
      { from: "Buyer bank",                       to: "verqo Capital pool",   amount: "$10,200",  method: "ACH debit on due date", time: "T+30", ledger: "Repays AR + carry/fee. credit: AR · debit: Capital pool · debit: Fee revenue" },
    ],
    providers: ["Modern Treasury (Virtual Accounts + Ledger)", "Verqo AI Underwriter"],
  },
  {
    id: "factoring",
    title: "Invoice factoring — Seller gets 97% on day 1",
    desc: "Seller opts in to factoring on a specific invoice. verqo advances 97% of face on D+1, collects from buyer on terms, retains 3% fee.",
    legs: [
      { from: "verqo Factoring pool",  to: "Seller bank",          amount: "$9,700",   method: "ACH credit (MT)",        time: "T+1",  ledger: "credit: Factoring pool · debit: AP. Open AR vs Buyer for $10,000." },
      { from: "Buyer bank",            to: "verqo Factoring pool", amount: "$10,000",  method: "ACH debit on due date",  time: "T+30", ledger: "credit: AR · debit: Factoring pool · debit: Fee $300 → revenue" },
    ],
    providers: ["Modern Treasury"],
  },
  {
    id: "wallet",
    title: "Wallet — Topup, hold, withdraw",
    desc: "Buyers and Sellers each have a verqo Wallet (Modern Treasury Virtual Account). Used for instant balances, refunds, partial payments.",
    legs: [
      { from: "User bank",          to: "User Wallet (MT VA)",  amount: "$5,000",   method: "ACH debit / Wire / Card", time: "T+0–T+1", ledger: "credit: User funds · debit: Wallet liability" },
      { from: "User Wallet (MT VA)",to: "Seller / Pay invoice", amount: "$1,200",   method: "Internal MT transfer",            time: "T+0",  ledger: "credit: Wallet liability · debit: Counterparty AP" },
      { from: "User Wallet (MT VA)",to: "User bank",            amount: "Variable", method: "ACH credit (withdraw)",            time: "T+1",  ledger: "credit: Wallet liability · debit: Cash" },
    ],
    providers: ["Modern Treasury (Virtual Accounts)"],
  },
  {
    id: "insurance",
    title: "Trade credit insurance — Premium and payout",
    desc: "Optional per-invoice insurance. Premium charged at activation. Claim payout from insurance reserve if buyer defaults.",
    legs: [
      { from: "Seller / Buyer wallet", to: "verqo Insurance reserve", amount: "0.1–1.2% of face", method: "Wallet debit", time: "T+0", ledger: "credit: Wallet · debit: Insurance reserve" },
      { from: "verqo Insurance reserve", to: "Seller bank",          amount: "Up to 90% of face", method: "ACH credit (MT)", time: "On claim approval", ledger: "credit: Insurance reserve · debit: AP" },
    ],
    providers: ["Modern Treasury", "Internal underwriter (mocked)"],
  },
];

const ACTORS = [
  { id: "buyer",    name: "Buyer",       role: "Pays for goods on terms or upfront", icon: ShoppingCart, color: "text-emerald-700 bg-emerald-500/10 ring-emerald-500/30" },
  { id: "seller",   name: "Seller",      role: "Receives D+1 payouts (or 97% via factoring)", icon: Building2, color: "text-amber-700 bg-amber-500/10 ring-amber-500/30" },
  { id: "platform", name: "verqo Platform", role: "Operating + Capital + Factoring + Insurance accounts", icon: Layers, color: "text-foreground bg-foreground/5 ring-foreground/20" },
  { id: "mt",       name: "Modern Treasury", role: "Virtual accounts, ledger, payment orders", icon: Banknote, color: "text-sky-700 bg-sky-500/10 ring-sky-500/30" },
  { id: "ai",       name: "Verqo AI Underwriter", role: "AI-driven memo + auto-grant rules", icon: Bot, color: "text-rose-700 bg-rose-500/10 ring-rose-500/30" },
];

export default function FlowOfFunds() {
  const [active, setActive] = useState("happy-path");
  const flow = FLOWS.find(f => f.id === active);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-foreground/10">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="fof-back">
            <div className="w-7 h-7 rounded-md bg-foreground grid place-items-center">
              <span className="text-accent font-bold text-[13px] display">v</span>
            </div>
            <span className="display font-semibold text-[16px] tracking-tight">verqo</span>
            <span className="text-foreground/40 mx-1.5">/</span>
            <span className="display text-[15px] tracking-tight">Flow of Funds</span>
          </Link>
          <div className="flex items-center gap-3 text-[12px] text-foreground/60">
            <span className="hidden md:inline mono uppercase tracking-[0.18em] text-[10.5px]">Partner-shareable</span>
            <Link to="/architecture" className="text-foreground/80 hover:text-foreground inline-flex items-center gap-1" data-testid="fof-arch-link">
              Full architecture <ArrowUpRight className="w-3.5 h-3.5"/>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-6">
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--accent,#e8ff3a)]"/>
          For Modern Treasury · Plaid review
        </div>
        <h1 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          How money moves on verqo.
        </h1>
        <p className="text-foreground/60 mt-4 max-w-2xl text-[15px] leading-relaxed">
          Every dollar on the platform passes through Modern Treasury's ledger.
          Below is the full taxonomy of money movement: counterparties, rails,
          timing, and the ledger entries we record on each leg.
        </p>
      </section>

      {/* Actors strip */}
      <section className="max-w-7xl mx-auto px-6 mt-8">
        <h2 className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">Actors in the system</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {ACTORS.map(a => {
            const Icon = a.icon;
            return (
              <div key={a.id} className="rounded-xl border border-foreground/10 bg-white p-4">
                <span className={`inline-grid place-items-center w-8 h-8 rounded-lg ring-1 ${a.color}`}>
                  <Icon className="w-4 h-4"/>
                </span>
                <div className="display text-[15px] tracking-tight mt-2.5">{a.name}</div>
                <p className="text-[12px] text-foreground/60 mt-1 leading-snug">{a.role}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Master diagram (always visible) */}
      <section className="max-w-7xl mx-auto px-6 mt-12">
        <h2 className="display text-2xl tracking-tight">Master diagram</h2>
        <p className="text-foreground/60 text-[14px] mt-1.5">Modern Treasury sits in the center. Every external rail enters and leaves through it.</p>

        <div className="mt-6 rounded-2xl border border-foreground/10 bg-white p-6 lg:p-10 relative overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-[0.04] pointer-events-none"/>
          <div className="relative grid lg:grid-cols-3 gap-8">
            {/* LEFT — Inflows */}
            <div>
              <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">Inflows</div>
              <div className="space-y-3">
                {[
                  { label: "Buyer bank → ACH debit",           sub: "NACHA same-day rail" },
                  { label: "Buyer card → tokenized",            sub: "PCI-tokenized · 3DS optional" },
                  { label: "Buyer wire → reference match",      sub: "MT Virtual Account routing" },
                  { label: "Lender → Capital pool funding",     sub: "Wholesale facility (future)" },
                ].map(x => (
                  <div key={x.label} className="rounded-lg border border-foreground/10 p-3.5 bg-secondary/30">
                    <div className="text-[13.5px] font-medium">{x.label}</div>
                    <div className="text-[11.5px] text-foreground/55 mt-0.5">{x.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* CENTER — MT spine */}
            <div className="relative">
              <div className="rounded-2xl bg-foreground text-background p-6 ring-1 ring-foreground">
                <div className="flex items-center gap-2 text-[10.5px] mono uppercase tracking-[0.22em] text-accent">
                  <Banknote className="w-3.5 h-3.5"/>The ledger spine
                </div>
                <div className="display text-2xl tracking-tight mt-2">Modern Treasury</div>
                <div className="text-[12.5px] text-background/60 mt-1.5">Source of truth for every dollar.</div>

                <ul className="mt-5 space-y-2">
                  {[
                    "verqo Operating Account",
                    "verqo Capital Pool (Net-30/60/90)",
                    "verqo Factoring Pool",
                    "verqo Insurance Reserve",
                    "verqo Fee Account",
                    "Per-user Wallet (Virtual Accounts)",
                  ].map(s => (
                    <li key={s} className="flex items-center gap-2 text-[13px] text-background/85">
                      <span className="w-1 h-1 rounded-full bg-accent"/>
                      {s}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 grid grid-cols-2 gap-3 text-[11.5px]">
                  <div className="rounded-md bg-background/10 p-2.5">
                    <div className="mono uppercase tracking-[0.18em] text-accent/90 text-[10px]">Per-leg</div>
                    <div className="mt-0.5">Ledger entries (debit/credit)</div>
                  </div>
                  <div className="rounded-md bg-background/10 p-2.5">
                    <div className="mono uppercase tracking-[0.18em] text-accent/90 text-[10px]">Per-payout</div>
                    <div className="mt-0.5">Payment Orders (ACH / Wire)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — Outflows */}
            <div>
              <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">Outflows</div>
              <div className="space-y-3">
                {[
                  { label: "Seller bank ← ACH credit",          sub: "D+1 default · Same-day on premium" },
                  { label: "User bank ← Wallet withdraw",        sub: "MT Payment Order, T+1" },
                  { label: "Insurance payout ← Reserve",         sub: "ACH on claim approval" },
                  { label: "verqo revenue ← Fees",               sub: "Internal MT transfer" },
                ].map(x => (
                  <div key={x.label} className="rounded-lg border border-foreground/10 p-3.5 bg-secondary/30">
                    <div className="text-[13.5px] font-medium">{x.label}</div>
                    <div className="text-[11.5px] text-foreground/55 mt-0.5">{x.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Arrows */}
          <div className="hidden lg:flex absolute inset-0 pointer-events-none items-center justify-between px-6">
            <div className="ml-[33%] -mr-2 text-foreground/30"><ArrowRight className="w-6 h-6"/></div>
            <div className="mr-[33%] -ml-2 text-foreground/30"><ArrowRight className="w-6 h-6"/></div>
          </div>
        </div>
      </section>

      {/* Flow tabs */}
      <section className="max-w-7xl mx-auto px-6 mt-14">
        <h2 className="display text-2xl tracking-tight">Money paths in detail</h2>
        <p className="text-foreground/60 text-[14px] mt-1.5">Pick a scenario to see every leg, rail, and ledger entry.</p>

        <div className="mt-5 flex flex-wrap gap-1.5 border-b border-foreground/10 pb-2">
          {FLOWS.map(f => {
            const isActive = active === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setActive(f.id)}
                data-testid={`fof-tab-${f.id}`}
                className={`px-3.5 py-2 rounded-full text-[13px] transition-all ${isActive ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground hover:bg-secondary/50"}`}
              >
                {f.title.split(" — ")[0]}
              </button>
            );
          })}
        </div>

        {flow && (
          <div className="mt-6 rounded-2xl border border-foreground/10 bg-white overflow-hidden">
            <div className="p-6 lg:p-8 border-b border-foreground/10">
              <div className="display text-xl tracking-tight">{flow.title}</div>
              <p className="text-foreground/65 text-[14px] mt-2 max-w-3xl">{flow.desc}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {flow.providers.map(p => (
                  <span key={p} className="text-[11px] mono uppercase tracking-[0.16em] px-2 py-1 rounded border border-foreground/15 bg-secondary/40">{p}</span>
                ))}
              </div>
            </div>

            {/* Legs */}
            <ol className="divide-y divide-foreground/5">
              {flow.legs.map((leg, i) => (
                <li key={i} className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-5 lg:p-6 hover:bg-secondary/20">
                  <div className="lg:col-span-1">
                    <div className="w-8 h-8 rounded-full bg-foreground text-background grid place-items-center text-[12px] mono">{i + 1}</div>
                  </div>
                  <div className="lg:col-span-4">
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">From → To</div>
                    <div className="mt-1 text-[14px] flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{leg.from}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-foreground/40"/>
                      <span className="font-medium">{leg.to}</span>
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Amount</div>
                    <div className="display text-[18px] tracking-tight mt-1">{leg.amount}</div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Method</div>
                    <div className="text-[13px] mt-1 text-foreground/85">{leg.method}</div>
                  </div>
                  <div className="lg:col-span-1">
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Timing</div>
                    <div className="text-[13px] mt-1 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-foreground/55"/>{leg.time}</div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Ledger entry</div>
                    <code className="block mono text-[11px] text-foreground/70 mt-1 leading-snug">{leg.ledger}</code>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>

      {/* Provider responsibilities */}
      <section className="max-w-7xl mx-auto px-6 mt-14">
        <h2 className="display text-2xl tracking-tight">Provider responsibilities</h2>
        <p className="text-foreground/60 text-[14px] mt-1.5">Who does what in the stack.</p>
        <div className="mt-5 rounded-2xl border border-foreground/10 bg-white overflow-hidden">
          <table className="w-full text-[13.5px]">
            <thead className="bg-secondary/40 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
              <tr>
                <th className="text-left p-4">Provider</th>
                <th className="text-left p-4">Role</th>
                <th className="text-left p-4">Touches</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/5">
              {[
                { p: "Modern Treasury", r: "Ledger of record · Virtual Accounts · Payment Orders", t: "Operating, Capital, Factoring, Insurance, Fee, every Wallet" },
                { p: "Plaid",           r: "Bank linking · Identity · Cashflow signals",            t: "Onboarding, KYC supplemental, AI underwriting input" },
                { p: "Middesk",         r: "KYB · UCC · Business credit",                           t: "Onboarding, AI underwriting input" },
                { p: "Verqo AI",        r: "Underwriting memo + auto-grant rules",                  t: "Sets buyer credit limit → unlocks Capital pool flow" },
                { p: "Verqo Freight",   r: "Parcel & Freight rails",                                t: "Not financial — informs invoice timing" },
              ].map(row => (
                <tr key={row.p} className="hover:bg-secondary/20">
                  <td className="p-4 font-medium">{row.p}</td>
                  <td className="p-4 text-foreground/80">{row.r}</td>
                  <td className="p-4 text-foreground/65">{row.t}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Reconciliation note */}
      <section className="max-w-7xl mx-auto px-6 mt-14">
        <div className="rounded-2xl bg-foreground text-background p-8 lg:p-10 relative overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-[0.07] pointer-events-none"/>
          <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full bg-accent/30 blur-3xl"/>
          <div className="relative max-w-3xl">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-accent/90 inline-flex items-center gap-2">
              <Lock className="w-3.5 h-3.5"/>Reconciliation & controls
            </div>
            <h3 className="display text-3xl tracking-tight mt-3 leading-tight">
              Every flow is double-entry, idempotent, and fully reconciled to MT statements.
            </h3>
            <ul className="mt-6 space-y-2.5 text-[14px] text-background/85">
              <li className="flex items-start gap-2.5"><Sparkles className="w-4 h-4 mt-1 text-accent shrink-0"/>Every leg writes a ledger entry in MT before money moves. Failed transfers revert with compensating entries.</li>
              <li className="flex items-start gap-2.5"><Sparkles className="w-4 h-4 mt-1 text-accent shrink-0"/>Per-user wallets are MT Virtual Accounts. Reconciliation against bank statements happens on every webhook.</li>
              <li className="flex items-start gap-2.5"><Sparkles className="w-4 h-4 mt-1 text-accent shrink-0"/>Capital pool tracks AR vs Buyer + AP vs Seller separately. Net exposure is reported daily to admins.</li>
              <li className="flex items-start gap-2.5"><Sparkles className="w-4 h-4 mt-1 text-accent shrink-0"/>All payment orders are idempotent — keyed off invoice ID + leg ID — so retries never double-pay.</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="border-t border-foreground/10 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 text-[12px] text-foreground/55 flex flex-wrap items-center justify-between gap-3">
          <div>Internal blueprint · Share with banking and ledger partners under NDA</div>
          <div className="mono uppercase tracking-[0.18em] text-[10.5px]">v1 · Feb 2026</div>
        </div>
      </footer>
    </div>
  );
}
